<template>
  <div class="tw-flex tw-flex-col tw-h-full tw-bg-white" data-test="partner-worker-details-panel">
    <div v-if="loading || error" tabindex="0">
      <div v-if="loading">
        <div class="gradient">
          <div class="profile-avatar tw-bg-gray-200 tw-rounded-full tw-animate-pulse"></div>
          <div class="tw-float-right tw-p-3">
            <WdsButtonIcon sm data-test="partner-worker-details-close" @click="emit('close')">
              <WdsIcon name="close" :size="16" />
            </WdsButtonIcon>
          </div>
        </div>

        <div class="detail-header">
          <WdsStack direction-row justify-space-between gap-sm>
            <div class="tw-basis-2/3">
              <WdsStack direction-column justify-space-between gap-xs>
                <div class="tw-h-7 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-48"></div>
                <div class="tw-h-5 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-32"></div>
              </WdsStack>
            </div>
          </WdsStack>
        </div>

        <WdsDivider />

        <div class="tw-p-8">
          <div class="tw-space-y-4">
            <Loading v-for="i in 8" :key="i" />
          </div>
        </div>
      </div>
      <div v-else>
        <div class="tw-text-right tw-p-3">
          <WdsButtonIcon sm data-test="partner-worker-details-close" @click="emit('close')">
            <WdsIcon name="close" :size="16" />
          </WdsButtonIcon>
        </div>
        <WdsAlert error class="tw-mx-4">
          {{ $t('An error occurred') }}
        </WdsAlert>
      </div>
    </div>

    <div v-else class="tw-flex tw-flex-col tw-h-full" tabindex="0">
      <div class="gradient">
        <WdsAvatar
          :path="worker?.avatarPath ?? ''"
          :initials="worker?.initials"
          :alt="workerName"
          class="profile-avatar"
          lg
        />
        <div class="tw-float-right tw-p-3">
          <WdsButtonIcon sm data-test="partner-worker-details-close" @click="emit('close')">
            <WdsIcon name="close" :size="16" />
          </WdsButtonIcon>
        </div>
      </div>

      <div class="detail-header">
        <WdsStack direction-row justify-space-between gap-sm>
          <div class="tw-basis-2/3">
            <WdsStack direction-column justify-space-between gap-xs>
              <WdsText :text="workerName" :title="workerName" xl />
              <WdsText :text="worker?.email || ''" :title="worker?.email || ''" large secondary />
            </WdsStack>
          </div>
        </WdsStack>
      </div>

      <WdsDivider />

      <div class="tw-flex-1 tw-overflow-y-auto">
        <div v-if="!loading && employmentCosts.length > 0" class="tw-p-8 tw-pb-0">
          <WdsText :text="$t('Employment cost')" medium secondary class="tw-mb-4" />

          <WdsGrid
            v-if="ongoingCostDisplay.amount || ongoingCostDisplay.percentage"
            columns="2"
            gap-sm
          >
            <WdsText :text="$t('Ongoing')" medium secondary />
            <div>
              <WdsText
                v-if="ongoingCostDisplay.amount"
                :text="ongoingCostDisplay.amount || '-'"
                medium
              />
              <WdsText
                v-if="ongoingCostDisplay.percentage"
                :text="ongoingCostDisplay.percentage || '-'"
                medium
              />
            </div>
          </WdsGrid>

          <WdsGrid v-if="oneTimeCostDisplay" columns="2" gap-sm>
            <WdsText :text="$t('One-time')" medium secondary />
            <WdsText :text="oneTimeCostDisplay || '-'" medium />
          </WdsGrid>
        </div>

        <div class="tw-p-8">
          <WdsText
            :text="$t('Employment details')"
            data-test="employment-details-title"
            medium
            secondary
            class="tw-mb-4"
          />

          <WdsGrid v-if="!loading && hasHires" columns="2" gap-sm>
            <WdsText :text="$t('Job title:')" medium secondary />
            <WdsText
              :text="
                currentContract?.jobName || currentHire?.job?.name
                  ? `${currentContract?.jobName || currentHire?.job?.name} ${currentHire?.number ? `#${currentHire.number}` : ''}`
                  : '-'
              "
              medium
            />

            <WdsText :text="$t('Contract:')" medium secondary />
            <WdsText :text="currentHire?.number ? `#${currentHire.number}` : '-'" medium />

            <WdsText :text="$t('Worksome hire ID:')" medium secondary />
            <WdsText :text="currentHire?.number ? `#${currentHire.number}` : '-'" medium />

            <WdsText :text="$t('Offer sent:')" medium secondary data-test="offer-sent-label" />
            <WdsText :text="offerSentDisplay" medium data-test="offer-sent-value" />

            <WdsText
              :text="$t('Offer accepted:')"
              medium
              secondary
              data-test="offer-accepted-label"
            />
            <WdsText :text="offerAcceptedDisplay" medium data-test="offer-accepted-value" />

            <WdsText :text="$t('Start date:')" medium secondary />
            <WdsText :text="startDateDisplay" medium />

            <WdsText :text="$t('End date:')" medium secondary />
            <WdsText :text="endDateDisplay" medium />

            <WdsText :text="$t('Location preference:')" medium secondary />
            <WdsText :text="locationPreferenceDisplay" medium />

            <WdsText :text="$t('Worker Location:')" medium secondary />
            <WdsText
              :text="
                worker?.address ? `${worker.address.city}, ${worker.address.country?.name}` : '-'
              "
              medium
            />

            <WdsText :text="$t('Assignment Location:')" medium secondary />
            <WdsText :text="currentContract?.location?.formattedAddress || '-'" medium />

            <WdsText :text="$t('Pay rate:')" medium secondary />
            <WdsText
              :text="
                currentContract?.rate && currentContract?.currency
                  ? `${currentContract.currency} ${currentContract.rate}`
                  : '-'
              "
              medium
            />

            <WdsText :text="$t('Pay type:')" medium secondary />
            <WdsText
              :text="currentContract?.rateType ? currentContract.rateType.toLowerCase() : '-'"
              medium
            />

            <WdsText :text="$t('Assignment company name:')" medium secondary />
            <WdsText :text="currentHire?.company?.name || '-'" medium />

            <WdsText :text="$t('Hiring manager:')" medium secondary />
            <WdsText :text="hiringManagerDisplay" medium />
          </WdsGrid>

          <div v-else-if="!loading && !hasHires">
            <WdsAlert warning>
              <template #default>{{ $t('No Employment Data Available') }}</template>
              <template #description>
                {{
                  $t(
                    'No hires or employment records found for this worker. This may indicate a data connectivity issue or the worker may not have any employment history with this partner.',
                  )
                }}
              </template>
            </WdsAlert>
          </div>
        </div>

        <WdsStack
          v-if="!loading && customFields.length > 0"
          direction-column
          gap-md
          class="tw-px-4 tw-w-full tw-py-8 tw-pt-0"
        >
          <WdsText :text="$t('Custom fields')" secondary />

          <CustomFieldValuesDisplay :custom-field-values="customFields" />
        </WdsStack>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  WdsButtonIcon,
  WdsIcon,
  WdsStack,
  WdsText,
  WdsAlert,
  WdsAvatar,
  WdsDivider,
  WdsGrid,
} from '@worksome/wds-components'
import { useQuery } from '@vue/apollo-composable'

import { PARTNER_WORKER_VIEW_FRAGMENT } from '@/components/WorkerDetailPanel/graphql/fragments'
import { PartnerWorkerViewQuery } from '@/components/WorkerDetailPanel/graphql/queries'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { useFragment } from '@/gql'
import useDate from '@/composables/useDate'
import { EmploymentCostType, EmploymentCostFrequency } from '@/gql/graphql'
import { locationPreferenceOptions } from '@/components/Job/constants'
import CustomFieldValuesDisplay from '@/components/Fieldable/CustomFieldValuesDisplay.vue'
import Loading from '@/components/Dashboard/Widgets/Utils/Loading.vue'

const props = defineProps<{
  workerId: string
  employmentId?: string
  tabKey?: string
}>()

const emit = defineEmits(['close'])

const variables = computed(() => ({
  workerId: props.workerId,
}))

const {
  loading,
  result: workerResult,
  error,
} = useQuery(PartnerWorkerViewQuery, variables, {
  clientId: 'publicApiCustomFields',
})

const worker = computed(() => useFragment(PARTNER_WORKER_VIEW_FRAGMENT, workerResult.value?.worker))

const workerName = computed(() => worker.value?.name ?? '')

const hires = computed(() => worker.value?.hires?.data ?? [])
const hasHires = computed(() => hires.value.length > 0)
const currentHire = computed(() => {
  if (props.employmentId) {
    return (
      hires.value.find((hire) => hire.employment?.id === props.employmentId) ??
      hires.value[0] ??
      null
    )
  }
  return hires.value[0] ?? null
})
const currentContract = computed(() => currentHire.value?.activeContract ?? null)
const customFields = computed(() => currentHire.value?.job?.customFieldValues ?? [])
const startDateFormatted = useDate(
  computed(() => currentContract.value?.startDate || currentHire.value?.startDate),
  'YYYY-MM-DD',
)
const endDateFormatted = useDate(
  computed(() => currentContract.value?.endDate || currentHire.value?.endDate),
  'YYYY-MM-DD',
)

const startDateDisplay = computed(() =>
  startDateFormatted.isValid.value ? startDateFormatted.date.value : '--',
)
const endDateDisplay = computed(() =>
  endDateFormatted.isValid.value ? endDateFormatted.date.value : '--',
)

const offerSentDateFormatted = useDate(
  computed(() => currentHire.value?.offeredAt),
  'YYYY-MM-DD',
)
const offerSentDisplay = computed(() =>
  offerSentDateFormatted.isValid.value ? offerSentDateFormatted.date.value : '-',
)

const offerAcceptedDateFormatted = useDate(
  computed(() => currentContract.value?.workerAcceptedAt),
  'YYYY-MM-DD',
)
const offerAcceptedDisplay = computed(() =>
  offerAcceptedDateFormatted.isValid.value ? offerAcceptedDateFormatted.date.value : '-',
)
const hiringManagerDisplay = computed(() => {
  const managers = currentHire.value?.job?.owners ?? []
  if (managers.length === 0) return '-'
  if (managers.length === 1) return managers[0]?.name ?? '-'
  return `${managers[0]?.name ?? ''} +${managers.length - 1}`
})

const locationPreferenceDisplay = computed(() => {
  const preference = currentContract.value?.locationPreference
  if (!preference) return '-'

  const option = locationPreferenceOptions.find((opt) => opt.value === preference)
  return option?.label || preference
})

const employmentCosts = computed(() => currentHire.value?.employment?.employmentCosts?.data ?? [])

const ongoingCostDisplay = computed(() => {
  const costs = employmentCosts.value
  const currency = currentContract.value?.currency || currentHire.value?.currency || null

  if (costs.length === 0) {
    return {
      amount: null,
      percentage: null,
    }
  }

  const ongoingFixedCosts = costs.filter(
    (cost) =>
      cost.frequency === EmploymentCostFrequency.Ongoing && cost.type === EmploymentCostType.Fixed,
  )
  const totalFixed = ongoingFixedCosts.reduce((sum, cost) => sum + cost.amount, 0)

  const ongoingPercentageCosts = costs.filter(
    (cost) =>
      cost.frequency === EmploymentCostFrequency.Ongoing &&
      cost.type === EmploymentCostType.Percentage,
  )
  const totalPercentage = ongoingPercentageCosts.reduce((sum, cost) => sum + cost.amount, 0)

  return {
    amount:
      totalFixed > 0
        ? `${currency || 'Unknown currency'} ${totalFixed.toFixed(2)} / invoice`
        : null,
    percentage: totalPercentage > 0 ? `${totalPercentage.toFixed(2)} % / invoice` : null,
  }
})

const oneTimeCostDisplay = computed(() => {
  const costs = employmentCosts.value
  const currency = currentContract.value?.currency || currentHire.value?.currency || null

  if (costs.length === 0) {
    return null
  }

  const oneTimeCosts = costs.filter((cost) => cost.frequency === EmploymentCostFrequency.OneTime)
  if (oneTimeCosts.length > 0) {
    const totalOneTime = oneTimeCosts.reduce((sum, cost) => sum + cost.amount, 0)
    return `${currency || 'Unknown currency'} ${totalOneTime.toFixed(2)}`
  }

  return null
})
</script>

<style scoped lang="scss">
.profile-avatar {
  @apply tw-w-20;
  @apply tw-h-20;
  @apply tw-top-[50px];
  @apply tw-absolute;
  @apply tw-ml-7;
}

.gradient {
  @apply tw-shrink-0;
  @apply tw-h-[100px];
  @apply tw-bg-gradient-to-r tw-from-indigo-400 tw-to-red-300;

  :deep(.wds-button-icon-container) {
    .wds-button-icon {
      @apply tw-bg-black/10;
    }

    &:hover {
      .wds-button-icon {
        @apply tw-bg-black/[0.14];
      }
    }
  }
}

.detail-header {
  @apply tw-px-7;
  @apply tw-pb-7;
  @apply tw-mt-8;
  @apply tw-pt-1.5;
}
</style>
